import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { MailWarning } from "lucide-react";
import { toast } from "sonner";
import { friendlyAuthError } from "@/lib/auth-errors";
import { trackFunnel } from "@/lib/funnel";

/**
 * Soft email verification.
 *
 * Signup no longer blocks on the confirmation link — users land in the app
 * immediately. This banner is the nudge: it emails a 6-digit code, and on a
 * successful check we stamp profiles.email_verified_at ourselves (Supabase's
 * email_confirmed_at is set automatically in auto-confirm mode, so it can't
 * tell us whether the address is real).
 */
export function EmailVerifyBanner({
  email,
  onVerified,
}: {
  email: string;
  onVerified?: () => void;
}) {
  const [stage, setStage] = useState<"idle" | "code">("idle");
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);

  const sendCode = async () => {
    setBusy(true);
    const { error } = await supabase.auth.signInWithOtp({
      email,
      options: { shouldCreateUser: false },
    });
    setBusy(false);
    if (error) return toast.error(friendlyAuthError(error));
    setStage("code");
    toast.success("We emailed you a 6-digit code");
  };

  const confirmCode = async () => {
    const token = code.trim();
    if (token.length < 6) return toast.error("Enter the 6-digit code from your email");
    setBusy(true);
    const { error } = await supabase.auth.verifyOtp({ email, token, type: "email" });
    if (error) {
      setBusy(false);
      return toast.error(friendlyAuthError(error));
    }
    const { data: auth } = await supabase.auth.getUser();
    if (auth.user) {
      await supabase
        .from("profiles")
        .update({ email_verified_at: new Date().toISOString() } as never)
        .eq("id", auth.user.id);
    }
    setBusy(false);
    trackFunnel("email_verified", { method: "otp" }, { once: true });
    toast.success("Email verified — thanks!");
    onVerified?.();
  };

  return (
    <div className="sticky top-0 z-30 border-b border-warning/40 bg-warning/10 px-4 py-2.5 text-sm text-warning-foreground">
      <div className="flex flex-wrap items-center gap-3">
        <MailWarning className="h-4 w-4 shrink-0 text-warning" aria-hidden />
        <span className="flex-1 min-w-0">
          Your password is set. Verify <strong className="break-all">{email}</strong> so you can
          recover your account if you ever forget it.
        </span>
        {stage === "idle" ? (
          <Button size="sm" variant="outline" onClick={sendCode} disabled={busy} className="h-8">
            {busy ? "Sending…" : "Send code"}
          </Button>
        ) : (
          <div className="flex w-full items-center gap-2 sm:w-auto">
            <Input
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
              inputMode="numeric"
              autoComplete="one-time-code"
              placeholder="123456"
              aria-label="Verification code"
              className="h-8 w-28 bg-background"
            />
            <Button size="sm" onClick={confirmCode} disabled={busy} className="h-8">
              {busy ? "Checking…" : "Confirm"}
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={sendCode}
              disabled={busy}
              className="h-8 px-2"
            >
              Resend
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
